import { useState, useEffect } from 'react';
import { Image, Plus, Edit, Trash2, Eye, EyeOff, Upload, GripVertical } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API, authFetch, getAuthHeaders } from './helpers';

const CATEGORIES = [
  { id: 'retreat', label: 'Elevated Escape' },
  { id: 'outdoors', label: 'Outdoors' },
  { id: 'events', label: 'Community Events' },
];

const GalleryTab = () => {
  const [photos, setPhotos] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ src: '', alt: '', category: 'events', sort_order: 0, is_visible: true });
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all');

  const fetchPhotos = async () => {
    const res = await authFetch(`${API}/gallery?visible_only=false`);
    if (res.ok) setPhotos(await res.json());
  };

  useEffect(() => { fetchPhotos(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ src: '', alt: '', category: 'events', sort_order: photos.length + 1, is_visible: true });
    setShowForm(true);
  };

  const openEdit = (photo) => {
    setEditing(photo);
    setForm({ src: photo.src, alt: photo.alt || '', category: photo.category, sort_order: photo.sort_order || 0, is_visible: photo.is_visible !== false });
    setShowForm(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/upload`, { method: 'POST', headers: getAuthHeaders(), body: fd });
      if (res.ok) {
        const data = await res.json();
        setForm(prev => ({ ...prev, src: data.url }));
      }
    } catch {}
    setUploading(false);
  };

  const savePhoto = async (e) => {
    e.preventDefault();
    if (!form.src) return;
    if (editing) {
      await authFetch(`${API}/gallery/${editing.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    } else {
      await authFetch(`${API}/gallery`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    }
    setShowForm(false); setEditing(null); fetchPhotos();
  };

  const toggleVisibility = async (photo) => {
    await authFetch(`${API}/gallery/${photo.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ is_visible: !photo.is_visible }) });
    fetchPhotos();
  };

  const deletePhoto = async (id) => {
    if (!window.confirm('Remove this photo from the gallery?')) return;
    await authFetch(`${API}/gallery/${id}`, { method: 'DELETE' });
    fetchPhotos();
  };

  const filtered = filter === 'all' ? photos : photos.filter(p => p.category === filter);
  const visibleCount = photos.filter(p => p.is_visible).length;

  return (
    <div data-testid="gallery-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-brand-dark">Gallery</h2>
          <p className="text-sm text-gray-500">{photos.length} photos ({visibleCount} visible on site)</p>
        </div>
        <button onClick={openNew} className="flex items-center gap-2 bg-brand-gold px-4 py-2 text-sm font-medium text-brand-dark hover:opacity-90" data-testid="add-photo-btn">
          <Plus className="w-4 h-4" /> Add Photo
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white p-6 mb-6" data-testid="gallery-photo-form">
          <h3 className="text-lg font-medium text-brand-dark mb-4">{editing ? 'Edit Photo' : 'Add New Photo'}</h3>
          <form onSubmit={savePhoto} className="space-y-4">
            <div>
              <Label className="text-brand-dark font-medium">Photo</Label>
              <div className="mt-2 flex items-start gap-4">
                {form.src && (
                  <img src={form.src} alt="Preview" className="w-32 h-24 object-cover border border-gray-200 flex-shrink-0" />
                )}
                <div className="flex-1 space-y-2">
                  <label className="flex items-center gap-2 bg-white border border-gray-300 px-4 py-2 text-sm font-medium text-brand-dark hover:bg-gray-50 cursor-pointer w-fit">
                    <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload Image'}
                    <input type="file" accept="image/*" onChange={handleUpload} className="hidden" disabled={uploading} data-testid="photo-upload-input" />
                  </label>
                  <p className="text-xs text-gray-400">Or paste an image URL below</p>
                  <Input value={form.src} onChange={(e) => setForm({...form, src: e.target.value})} placeholder="https://..." className="text-sm" data-testid="photo-url-input" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-brand-dark font-medium">Caption / Alt Text</Label>
                <Input value={form.alt} onChange={(e) => setForm({...form, alt: e.target.value})} className="mt-1" placeholder="Describe this photo" data-testid="photo-alt-input" />
              </div>
              <div>
                <Label className="text-brand-dark font-medium">Category</Label>
                <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="mt-1 w-full border border-gray-300 p-2 rounded" data-testid="photo-category-select">
                  {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-brand-dark font-medium">Sort Order</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm({...form, sort_order: parseInt(e.target.value) || 0})} className="mt-1" data-testid="photo-sort-input" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input type="checkbox" id="photoVisible" checked={form.is_visible} onChange={(e) => setForm({...form, is_visible: e.target.checked})} className="w-4 h-4" data-testid="photo-visible-check" />
              <Label htmlFor="photoVisible">Visible on public gallery</Label>
            </div>
            <div className="flex gap-4">
              <button type="submit" className="bg-brand-gold px-6 py-2 text-sm font-medium text-brand-dark hover:opacity-90" data-testid="save-photo-btn">{editing ? 'Update' : 'Add Photo'}</button>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="bg-gray-200 px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Filter */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="gallery-admin-filters">
        {[{ id: 'all', label: 'All' }, ...CATEGORIES].map(c => (
          <button key={c.id} onClick={() => setFilter(c.id)} className={`px-3 py-1.5 text-xs font-medium ${filter === c.id ? 'bg-brand-dark text-white' : 'bg-white text-brand-dark hover:bg-gray-50'}`}>
            {c.label}
          </button>
        ))}
      </div>

      {/* Photo Grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-white">
          <Image className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No photos yet. Add your first photo above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-admin-grid">
          {filtered.map(photo => (
            <div key={photo.id} className={`bg-white group relative ${!photo.is_visible ? 'opacity-50' : ''}`} data-testid={`gallery-photo-${photo.id}`}>
              <div className="aspect-square overflow-hidden">
                <img src={photo.src} alt={photo.alt} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-3">
                <p className="text-xs text-gray-700 truncate">{photo.alt || 'No caption'}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500">{CATEGORIES.find(c => c.id === photo.category)?.label || photo.category}</span>
                  <span className="text-[10px] text-gray-400">#{photo.sort_order}</span>
                </div>
              </div>
              {/* Action overlay */}
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => toggleVisibility(photo)} className={`p-1.5 bg-white/90 shadow-sm ${photo.is_visible ? 'text-green-600 hover:text-red-600' : 'text-gray-400 hover:text-green-600'}`} title={photo.is_visible ? 'Hide' : 'Show'}>
                  {photo.is_visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => openEdit(photo)} className="p-1.5 bg-white/90 shadow-sm text-gray-400 hover:text-brand-dark" title="Edit" data-testid={`edit-photo-${photo.id}`}>
                  <Edit className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => deletePhoto(photo.id)} className="p-1.5 bg-white/90 shadow-sm text-gray-400 hover:text-red-600" title="Delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GalleryTab;
